import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ComposedChart, ScatterChart, Scatter } from 'recharts';
import LogoHeader from '../components/LogoHeader';
import getAuthToken from '../utils/getAuthToken';
import { setResultData } from '../thunks/test/actions';

const ResultPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sessionId, examId } = useParams();
  const { data: examData } = useSelector(state => state.test);
  const [resultData, setLocalResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedTopics, setExpandedTopics] = useState({});
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');
  const [showInsights, setShowInsights] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('accuracy');

  useEffect(() => {
    const fetchResult = async () => {
      try {
        setLoading(true);
        const token = getAuthToken();
        
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        // Hardcoded sessionId and examId for testing
        const sessionIdToUse = "687cdd321aa94ec3d3eb6b31_1754230096980_ahtemt1mi";
        const examIdToUse = "687cdd321aa94ec3d3eb6b31";

        const response = await axios.get(`/result/${sessionIdToUse}/${examIdToUse}`, {
          headers: {
            'Authorization': `${token}`
          }
        });

        setLocalResultData(response.data);
        dispatch(setResultData(response.data));
      } catch (err) {
        console.error('Error fetching result:', err);
        setError(err.response?.data?.message || 'Failed to load result');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [sessionId, examId, examData, dispatch]);

  const handleBackToHome = () => navigate('/');
  const handleQuestionReview = () => navigate('/question-review');

  const formatTime = (milliseconds) => {
    if (milliseconds <= 0) return '0m 0s';
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getPerformanceIcon = (percentage) => {
    if (percentage >= 80) return '🎉';
    if (percentage >= 60) return '👍';
    if (percentage >= 40) return '😐';
    return '😔';
  };

  const toggleTopicExpansion = (topicName) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicName]: !prev[topicName]
    }));
  };

  // Enhanced chart data preparation
  const getPerformancePieData = () => {
    if (!resultData) return [];
    return [
      { name: 'Correct', value: resultData.overallStats.correct, color: '#10B981' },
      { name: 'Incorrect', value: resultData.overallStats.incorrect, color: '#EF4444' },
      { name: 'Skipped', value: resultData.overallStats.skipped, color: '#6B7280' }
    ];
  };

  const getTopicPerformanceData = () => {
    if (!resultData?.topicwisePerformance) return [];
    return resultData.topicwisePerformance.map(topic => ({
      name: topic.topicName,
      accuracy: topic.accuracyScore,
      engagement: topic.engagementScore,
      confidence: topic.confidenceScore,
      speed: topic.speedScore
    }));
  };

  const getRadarData = () => {
    if (!resultData?.topicwisePerformance) return [];
    return resultData.topicwisePerformance.map(topic => ({
      subject: topic.topicName,
      accuracy: topic.accuracyScore,
      engagement: topic.engagementScore,
      confidence: topic.confidenceScore,
      speed: topic.speedScore
    }));
  };

  const getTimeTrendData = () => {
    if (!resultData?.topicwisePerformance) return [];
    return resultData.topicwisePerformance.map((topic, index) => ({
      name: topic.topicName,
      timeSpent: topic.speedScore,
      idealTime: 120, // Placeholder
      efficiency: (topic.speedScore / 120) * 100
    }));
  };

  const getLeaderboardData = () => {
    return [
      { name: 'Topper', score: 95, color: '#10B981' },
      { name: 'You', score: resultData?.percentage || 0, color: '#3B82F6' },
      { name: 'Class Avg', score: 72, color: '#F59E0B' }
    ];
  };

  const getDetailedBreakdown = () => {
    if (!resultData) return [];
    return [
      { name: 'Easy Questions', correct: 15, total: 20, percentage: 75 },
      { name: 'Medium Questions', correct: 12, total: 15, percentage: 80 },
      { name: 'Hard Questions', correct: 8, total: 15, percentage: 53 },
      { name: 'Very Hard Questions', correct: 5, total: 10, percentage: 50 }
    ];
  };

  const getTimeAnalysis = () => {
    if (!resultData) return [];
    return [
      { name: 'First 25%', time: 45, accuracy: 85 },
      { name: 'Second 25%', time: 52, accuracy: 78 },
      { name: 'Third 25%', time: 48, accuracy: 82 },
      { name: 'Last 25%', time: 55, accuracy: 75 }
    ];
  };

  const getWeakStrongTopics = () => {
    if (!resultData?.topicwisePerformance) return { weak: [], strong: [] };
    
    const topics = resultData.topicwisePerformance;
    const weak = topics.filter(t => t.accuracyScore < 70).sort((a, b) => a.accuracyScore - b.accuracyScore);
    const strong = topics.filter(t => t.accuracyScore >= 80).sort((a, b) => b.accuracyScore - a.accuracyScore);
    
    return { weak: weak.slice(0, 3), strong: strong.slice(0, 3) };
  };

  const getPerformanceInsights = () => {
    if (!resultData) return [];
    
    const insights = [];
    const accuracy = ((resultData.overallStats.correct / resultData.overallStats.totalQuestions) * 100).toFixed(1);
    
    if (accuracy >= 80) {
      insights.push({
        type: 'success',
        icon: '🎯',
        title: 'Excellent Performance',
        description: 'You\'re performing exceptionally well! Keep up the great work.'
      });
    } else if (accuracy >= 60) {
      insights.push({
        type: 'warning',
        icon: '📈',
        title: 'Good Performance',
        description: 'You\'re doing well, but there\'s room for improvement in certain areas.'
      });
    } else {
      insights.push({
        type: 'error',
        icon: '💪',
        title: 'Need Improvement',
        description: 'Focus on weak areas and practice more to improve your performance.'
      });
    }

    if (resultData.overallStats.averageTimePerQuestion > 120000) {
      insights.push({
        type: 'warning',
        icon: '⏰',
        title: 'Time Management',
        description: 'You\'re spending more time than average per question. Consider improving time efficiency.'
      });
    }

    return insights;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LogoHeader />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Analytics Dashboard</h2>
            <p className="text-gray-600">Preparing your detailed performance insights...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LogoHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Unable to Load Analytics</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">{error}</p>
            <button 
              onClick={() => navigate('/start')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
            >
              Back to Start
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { weak, strong } = getWeakStrongTopics();
  const insights = getPerformanceInsights();

  return (
    <div className="min-h-screen bg-gray-50">
      <LogoHeader />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-4 mb-4 lg:mb-0">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{resultData.examTitle}</h1>
                <p className="text-gray-600">Performance Analytics Dashboard</p>
              </div>
        </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{resultData.percentage}%</div>
                <div className="text-sm text-gray-600">Overall Score</div>
              </div>
              <div className="text-4xl">{getPerformanceIcon(resultData.percentage)}</div>
            </div>
          </div>
          </div>
          
        {/* Performance Insights */}
        {showInsights && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Performance Insights</h3>
              <button
                onClick={() => setShowInsights(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(() => {
                const insights = [];
                const accuracy = ((resultData.overallStats.correct / resultData.overallStats.totalQuestions) * 100).toFixed(1);
                
                if (accuracy >= 80) {
                  insights.push({
                    type: 'success',
                    icon: '🎯',
                    title: 'Excellent Performance',
                    description: 'You\'re performing exceptionally well! Keep up the great work.'
                  });
                } else if (accuracy >= 60) {
                  insights.push({
                    type: 'warning',
                    icon: '📈',
                    title: 'Good Performance',
                    description: 'You\'re doing well, but there\'s room for improvement in certain areas.'
                  });
                } else {
                  insights.push({
                    type: 'error',
                    icon: '💪',
                    title: 'Need Improvement',
                    description: 'Focus on weak areas and practice more to improve your performance.'
                  });
                }

                if (resultData.overallStats.averageTimePerQuestion > 120000) {
                  insights.push({
                    type: 'warning',
                    icon: '⏰',
                    title: 'Time Management',
                    description: 'You\'re spending more time than average per question. Consider improving time efficiency.'
                  });
                }

                return insights.map((insight, index) => (
                  <div key={index} className={`p-4 rounded-lg border ${
                    insight.type === 'success' ? 'bg-green-50 border-green-200' :
                    insight.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">{insight.icon}</div>
                      <div>
                        <h4 className="font-medium text-gray-900">{insight.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{insight.description}</p>
                      </div>
                    </div>
          </div>
                ));
              })()}
            </div>
          </div>
        )}



        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm p-1 mb-6">
          <div className="flex space-x-1">
            {['overview', 'topic-wise', 'section-wise', 'comparison', 'detailed'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Questions</p>
                    <p className="text-2xl font-bold text-gray-900">{resultData.overallStats.totalQuestions}</p>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Static Data</span>
              </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
              </div>
            </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">Accuracy Rate</p>
                    <p className="text-2xl font-bold text-green-600">
                      {((resultData.overallStats.correct / resultData.overallStats.totalQuestions) * 100).toFixed(1)}%
                    </p>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Static Data</span>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">Time Efficiency</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatTime(resultData.overallStats.averageTimePerQuestion)}
                    </p>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Static Data</span>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">Engagement</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {resultData.overallStats.totalVisits}
                    </p>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Static Data</span>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Performance Distribution */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getPerformancePieData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getPerformancePieData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Topic Balance Radar */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Subject Balance Analysis</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={getRadarData()}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis />
                    <Radar name="Accuracy" dataKey="accuracy" stroke="#10B981" fill="#10B981" fillOpacity={0.3} />
                    <Radar name="Engagement" dataKey="engagement" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
                    <Radar name="Confidence" dataKey="confidence" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.3} />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Weak & Strong Topics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Weak Topics */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Areas for Improvement</h3>
            <div className="space-y-4">
                  {(() => {
                    const topics = resultData.topicwisePerformance || [];
                    const weak = topics.filter(t => t.accuracyScore < 70).sort((a, b) => a.accuracyScore - b.accuracyScore).slice(0, 3);
                    
                    return weak.map((topic, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <span className="font-medium text-gray-900">{topic.topicName}</span>
                        </div>
                        <span className="text-red-600 font-semibold">{topic.accuracyScore}%</span>
                      </div>
                    ));
                  })()}
                  {(() => {
                    const topics = resultData.topicwisePerformance || [];
                    const weak = topics.filter(t => t.accuracyScore < 70);
                    return weak.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No weak areas identified! 🎉</p>
                    );
                  })()}
                </div>
              </div>

              {/* Strong Topics */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Strong Areas</h3>
                <div className="space-y-4">
                  {(() => {
                    const topics = resultData.topicwisePerformance || [];
                    const strong = topics.filter(t => t.accuracyScore >= 80).sort((a, b) => b.accuracyScore - a.accuracyScore).slice(0, 3);
                    
                    return strong.map((topic, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span className="font-medium text-gray-900">{topic.topicName}</span>
                        </div>
                        <span className="text-green-600 font-semibold">{topic.accuracyScore}%</span>
                      </div>
                    ));
                  })()}
                  {(() => {
                    const topics = resultData.topicwisePerformance || [];
                    const strong = topics.filter(t => t.accuracyScore >= 80);
                    return strong.length === 0 && (
                      <p className="text-gray-500 text-center py-4">Keep working to identify strong areas!</p>
                    );
                  })()}
                </div>
              </div>
            </div>
            
            {/* Leaderboard and Time Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Leaderboard */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Comparison</h3>
            <div className="space-y-4">
                  {getLeaderboardData().map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: item.color }}></div>
                        <span className="font-medium text-gray-900">{item.name}</span>
                      </div>
                      <span className="text-xl font-bold" style={{ color: item.color }}>{item.score}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Time Trend */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Efficiency Trend</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={getTimeTrendData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="timeSpent" fill="#8B5CF6" name="Time Spent" />
                    <Line type="monotone" dataKey="idealTime" stroke="#EF4444" strokeWidth={2} name="Ideal Time" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Topic-wise Tab */}
        {activeTab === 'topic-wise' && (
          <div className="space-y-6">
            {resultData.topicwisePerformance?.map((topic, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleTopicExpansion(topic.topicName)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-4 h-4 rounded-full ${getPerformanceColor(topic.accuracyScore)}`}></div>
                      <h3 className="text-lg font-semibold text-gray-900">{topic.topicName}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPerformanceColor(topic.accuracyScore)}`}>
                        {topic.accuracyScore}% Accuracy
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Static Data</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Engagement</div>
                        <div className="font-semibold text-blue-600">{topic.engagementScore}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Confidence</div>
                        <div className="font-semibold text-green-600">{topic.confidenceScore}</div>
                      </div>
                      <svg 
                        className={`w-5 h-5 text-gray-400 transition-transform ${expandedTopics[topic.topicName] ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
            </div>
          </div>
        </div>

                {expandedTopics[topic.topicName] && (
                  <div className="border-t border-gray-100 p-6 bg-gray-50">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Question Analysis */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-4">Question Analysis</h4>
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-900">Question Statistics</h5>
                              <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">Static Data</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="text-center p-3 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">
                                  {Math.floor(topic.accuracyScore / 100 * 15)}
                                </div>
                                <div className="text-sm text-gray-600">Correct</div>
                              </div>
                              <div className="text-center p-3 bg-red-50 rounded-lg">
                                <div className="text-2xl font-bold text-red-600">
                                  {15 - Math.floor(topic.accuracyScore / 100 * 15)}
                                </div>
                                <div className="text-sm text-gray-600">Incorrect</div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-white rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-900">Time Analysis</h5>
                              <span className="px-2 py-1 bg-purple-100 text-purple-600 text-xs rounded-full">Static Data</span>
                            </div>
            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Average Time per Question</span>
                                <span className="font-semibold text-purple-600">{Math.floor(topic.speedScore / 10)}m {topic.speedScore % 10}s</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Fastest Question</span>
                                <span className="font-semibold text-green-600">Q{Math.floor(Math.random() * 15) + 1} - 45s</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Slowest Question</span>
                                <span className="font-semibold text-red-600">Q{Math.floor(Math.random() * 15) + 1} - 3m 20s</span>
                              </div>
              </div>
              </div>
              </div>
            </div>
            
                      {/* Performance Metrics */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-4">Performance Metrics</h4>
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-900">Accuracy Breakdown</h5>
                              <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full">Static Data</span>
                            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Easy Questions</span>
                                <span className="font-semibold text-green-600">{(topic.accuracyScore + 10)}%</span>
              </div>
              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Medium Questions</span>
                                <span className="font-semibold text-blue-600">{topic.accuracyScore}%</span>
              </div>
              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Hard Questions</span>
                                <span className="font-semibold text-red-600">{(topic.accuracyScore - 15)}%</span>
                              </div>
              </div>
            </div>
            
                          <div className="bg-white rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-900">Engagement Details</h5>
                              <span className="px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded-full">Static Data</span>
                            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Questions Revisited</span>
                                <span className="font-semibold text-orange-600">{Math.floor(topic.engagementScore / 10)}</span>
              </div>
              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Answer Changes</span>
                                <span className="font-semibold text-yellow-600">{Math.floor(topic.engagementScore / 15)}</span>
              </div>
              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Time Spent</span>
                                <span className="font-semibold text-purple-600">{Math.floor(topic.speedScore * 1.5)}m</span>
                              </div>
                            </div>
              </div>
            </div>
          </div>
        </div>

                    {/* Recommendations */}
                    <div className="mt-6 bg-white rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Recommendations</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {topic.accuracyScore < 70 && (
                          <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <div>
                              <div className="font-medium text-red-700">Focus on Weak Areas</div>
                              <div className="text-sm text-red-600">Practice more questions in this topic</div>
                    </div>
                  </div>
                        )}
                        {topic.engagementScore < 60 && (
                          <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                            <div>
                              <div className="font-medium text-yellow-700">Improve Engagement</div>
                              <div className="text-sm text-yellow-600">Review questions more thoroughly</div>
                    </div>
                    </div>
                        )}
                        {topic.accuracyScore >= 80 && (
                          <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <div>
                              <div className="font-medium text-green-700">Excellent Performance</div>
                              <div className="text-sm text-green-600">Keep up the great work!</div>
                    </div>
                  </div>
                        )}
                        <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <div>
                            <div className="font-medium text-blue-700">Time Management</div>
                            <div className="text-sm text-blue-600">Average {Math.floor(topic.speedScore / 10)}m per question</div>
                      </div>
                    </div>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              ))}
          </div>
        )}

        {/* Section-wise Tab */}
        {activeTab === 'section-wise' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Section Performance Analysis</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={getTopicPerformanceData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="accuracy" fill="#10B981" name="Accuracy %" />
                  <Bar dataKey="engagement" fill="#3B82F6" name="Engagement" />
                  <Bar dataKey="confidence" fill="#F59E0B" name="Confidence" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Comparison Tab */}
        {activeTab === 'comparison' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance vs Peers</h3>
            <div className="space-y-4">
                  {getLeaderboardData().map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded-full`} style={{ backgroundColor: item.color }}></div>
                        <span className="font-medium text-gray-900">{item.name}</span>
                    </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold" style={{ color: item.color }}>{item.score}%</span>
                        {item.name === 'You' && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Current</span>
                        )}
                  </div>
                    </div>
                  ))}
                    </div>
                  </div>
                  
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Efficiency Comparison</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={getTimeTrendData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="efficiency" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
                </div>
            </div>
          </div>
        )}

        {/* Detailed Tab */}
        {activeTab === 'detailed' && (
          <div className="space-y-6">
            {/* Difficulty Breakdown */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance by Difficulty Level</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getDetailedBreakdown()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="percentage" fill="#10B981" name="Accuracy %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Time vs Accuracy Scatter */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Time vs Accuracy Analysis</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart data={getTimeAnalysis()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" name="Time (minutes)" />
                  <YAxis dataKey="accuracy" name="Accuracy %" />
                  <Tooltip />
                  <Scatter dataKey="accuracy" fill="#3B82F6" />
                </ScatterChart>
              </ResponsiveContainer>
                </div>

            {/* Detailed Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Question Analysis</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Questions Attempted</span>
                    <span className="font-semibold">{resultData.overallStats.totalQuestions - resultData.overallStats.skipped}</span>
                        </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Questions Skipped</span>
                    <span className="font-semibold text-yellow-600">{resultData.overallStats.skipped}</span>
                        </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average Time per Question</span>
                    <span className="font-semibold">{formatTime(resultData.overallStats.averageTimePerQuestion)}</span>
                      </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Time Spent</span>
                    <span className="font-semibold">{formatTime(resultData.totalTimeSpent)}</span>
                            </div>
                          </div>
                      </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Metrics</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Question Visits</span>
                    <span className="font-semibold">{resultData.overallStats.totalVisits}</span>
                        </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average Visits per Question</span>
                    <span className="font-semibold">{resultData.overallStats.averageVisitsPerQuestion.toFixed(1)}</span>
                        </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Most Visited Question</span>
                    <span className="font-semibold text-blue-600">Q{Math.floor(Math.random() * 50) + 1}</span>
                        </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Least Visited Question</span>
                    <span className="font-semibold text-green-600">Q{Math.floor(Math.random() * 50) + 1}</span>
                    </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleQuestionReview}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>Review Questions</span>
              </div>
            </button>
            
          <button
            onClick={handleBackToHome}
              className="flex-1 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span>Back to Home</span>
              </div>
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultPage; 